import React, {
  MutableRefObject,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  memo, PropsWithChildren,
} from 'react';

import Swipe from 'app/lib/Swipe';
import { useDispatch } from 'app/store/utils/redux/hooks';

import Controller, { IControllerConfig } from '../lib/Controller';

import Item from '../item/Item';
import styles from './Container.module.css';

export {
  Controller,
};

type ContainerProps = {
  width: number,
  ratio: number,
  unit: 'px' | 'em' | 'rem',
  controllerRef?: MutableRefObject<ReactNode | Controller>,
  onFlip?: (event: { currentPage: number, visiblePages: number[], countPages?: number, }) => void,
  initConfig?: IControllerConfig,
};

const Container = memo((props: PropsWithChildren<ContainerProps>) => {
  const {
    children,
    width,
    ratio,
    unit = 'px',
    controllerRef,
    onFlip,
    initConfig = {},
  } = props;

  const height = width * ratio;

  const dispatch = useDispatch();

  const componentRef: any = useRef();
  const control = useRef<Controller>();
  const swipe = useRef<Swipe>();

  const handleStep = useCallback((event: { payload: any }) => {
    const { payload } = event;
  }, []);

  const handleVisibleUpdate = useCallback((event: { payload: any }) => {
    const { currentPage, visiblePages } = event.payload;
    if (onFlip) {
      onFlip({ currentPage, visiblePages });
    }
  }, [onFlip]);

  const handlePageAdd = useCallback((idx: number, item: any) => (
    <div
      key={`page-${idx}`}
      data-name="flipbook-page"
      data-id={`${idx}`}
      className={styles.page}
    >
      <Item controllerRef={control}>{item}</Item>
      <div className={styles.shadow} />
    </div>
  ), []);

  const handleFlipLeft = useCallback((data: any) => {
    control.current?.flipLeft();
  }, []);

  const handleFlipRight = useCallback((data: any) => {
    control.current?.flipRight();
  }, []);

  useLayoutEffect(() => {
    const count = React.Children.count(children);

    control.current = new Controller(styles.componentFlipBook, count, 0, {
      container: styles.componentFlipBook,
      page: styles.page,
      frontSide: styles.frontSide,
      backSide: styles.backSide,
      shadow: styles.shadow,
    }, initConfig);
    control.current.addEventListener(Controller.EVENT.MOVE_STEP, handleStep);
    control.current.addEventListener(Controller.EVENT.VISIBLE_UPDATE, handleVisibleUpdate);
    (componentRef.current as any).controller = control.current;
    if (controllerRef && 'current' in controllerRef) {
      controllerRef.current = control.current;
    }
    if (onFlip) {
      onFlip({
        currentPage: control.current.getCurrentPage(),
        visiblePages: control.current.getVisiblePages(),
        countPages: count,
      });
    }
    swipe.current = new Swipe(styles.componentFlipBook);
    swipe.current?.addEventListener(Swipe.LEFT, handleFlipLeft);
    swipe.current?.addEventListener(Swipe.RIGHT, handleFlipRight);

    return () => {
      if (controllerRef && controllerRef.current) {
        controllerRef.current = undefined;
      }
      if (control.current) {
        control.current?.destroy();
        control.current = undefined;
      }
      if (swipe.current) {
        swipe.current?.removeEventListener(Swipe.LEFT, handleFlipLeft);
        swipe.current?.removeEventListener(Swipe.RIGHT, handleFlipRight);
        swipe.current?.destroy();
        swipe.current = undefined;
      }
    };
  }, [dispatch, children, handleStep, handleVisibleUpdate, controllerRef, onFlip, initConfig, handleFlipLeft, handleFlipRight]);

  const pages: Array<ReactNode> = [];
  let idx = 0;
  React.Children.forEach(children, (item) => {
    pages.push(handlePageAdd(idx, item));
    idx += 1;
  });

  const style = {
    width: `${width}${width > 0 ? unit : ''}`,
    height: `${height}${height > 0 ? unit : ''}`,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.componentFlipBook}
      style={style}
      ref={componentRef}
    >
      {pages}
    </div>
  );
});

export default Container;
